<template>
  <DynamicFormList :items="alternatives_sync">
    <template #items="{ item, index }">
      <DynamicFormListItem #default="{ active, toggle }">
        <AlternativeListItem
          :item="item"
          :readonly="readonly"
          :editting="active"
          @edit="toggle"
          @remove="remove(item, index)"
          @create-action-plan="$emit('on-create-action-plan', item)"
        >
          <determination
            v-if="item.showDeterminate"
            :alternative-id="item.id"
            :is-read-only="readonly || !!item.determination"
            @save="(v) => saveDeterminate(v, item, index, toggle)"
            @cancel="cancelDeterminate(item, toggle)"
          />
          <AlternativeForm
            v-else
            ref="alternativeForm"
            editting
            :item="item"
            :viewmodel="formViewmodel"
            @setViewModelCategories="setViewModelCategories"
            @setViewModelReasons="setViewModelReasons"
            @setViewModelSalonSpecialties="setViewModelSalonSpecialties"
            @setViewModelNames="setViewModelNames"
            @setViewModelDocumentTypes="setViewModelDocumentTypes"
            @submit="edit(...arguments, index, toggle)"
            @cancel="cancel(item, toggle)"
          />
        </AlternativeListItem>
      </DynamicFormListItem>
    </template>

    <template #form="{ toggle }">
      <AlternativeForm
        ref="alternativeForm"
        :viewmodel="formViewmodel"
        @setViewModelCategories="setViewModelCategories"
        @setViewModelSalonSpecialties="setViewModelSalonSpecialties"
        @setViewModelNames="setViewModelNames"
        @setViewModelDocumentTypes="setViewModelDocumentTypes"
        @submit="add"
        @cancel="toggle"
      />
    </template>

    <template v-if="!readonly" #form-toggler="{ toggle }">
      <template v-if="alternatives_sync.length">
        <div class="text-right">
          <ActionButton
            text="Añadir"
            variant="success"
            i="fas fa-file-plus"
            @click="toggle"
          />
        </div>
      </template>
      <template v-else>
        <ActivationButton
          text="Presione aquí para añadir alternativa de ubicación"
          @click.stop="toggle"
        />
      </template>
    </template>
  </DynamicFormList>
</template>

<script>
import { DynamicFormList, DynamicFormListItem } from '@/components/common';
import ActionButton from 'ActionButton';
import { ActivationButton } from '@/components/common';
import AlternativeForm from './AlternativeForm';
import AlternativeListItem from './AlternativeListItem';
import Determination from './Determination.vue';
import alert from '@/mixin/alert.mixin';
import * as api from '@/views/pei/location-alternatives/_api';
import objectToFormData from 'object-to-formdata';

export default {
  name: 'LocationAlternativesAlternativeList',
  mixins: [alert],
  components: {
    DynamicFormList,
    DynamicFormListItem,
    ActionButton,
    ActivationButton,
    AlternativeForm,
    AlternativeListItem,
    Determination,
  },
  props: {
    readonly: { type: Boolean, default: false },
    viewmodel: { type: Object, required: true, default: null },
    alternatives: { type: Array, required: true, default: () => [] },
  },
  data: () => ({
    localViewmodel: {
      categories: [],
      reasons: [],
      salonSpecialties: [],
      names: [],
    },
  }),
  computed: {
    alternatives_sync: {
      get() {
        return this.alternatives.map((x) => ({
          ...x,
          showChangeCapacity: false,
          showDeterminate: false,
        }));
      },
      set(value) {
        this.$emit('update:alternatives', value);
      },
    },
    formViewmodel() {
      return {
        ...this.localViewmodel,
        academicYearId: this.viewmodel?.academicYear?.id,
        availableGrades: this.viewmodel?.availableGrades ?? [],
      };
    },
  },
  methods: {
    async saveDeterminate(value, item, index, toggle) {
      const payload = {
        ...value,
        documents:
          value.hasHipaa && value.documents?.length
            ? value.documents
            : undefined,
        improvements:
          !value.hasHipaa && value.improvements?.length
            ? value.improvements
            : undefined,
      };
      const formData = objectToFormData(payload, { indices: true });
      if (value.documents?.length) {
        value.documents.forEach((doc, i) =>
          formData.append(`documents[${i}].file`, doc.file)
        );
      }

      try {
        const { data } = await api.saveDetermination(item.id, formData);
        this.ShowToast(
          '',
          'La alternativa de ubicación ha sido determinada exitosamente.',
          'success'
        );
        item.determination = data;
        const arr = [];
        this.alternatives_sync.forEach((x, i) =>
          arr.push(i === index ? item : x)
        );
        this.$emit('update:alternatives', arr);
        this.cancelDeterminate(item, toggle);
      } catch {
        this.ShowToast(
          'Error',
          'No se pudo determinar la alternativa de ubicación.',
          'error'
        );
      }
    },
    cancelDeterminate(item, toggle) {
      item.showDeterminate = false;
      toggle();
    },
    async add(item) {
      if (this.readonly) return;
      await this.saveUpdate(item);
    },
    async edit(item, index) {
      if (this.readonly) return;
      const result = await this.saveUpdate(item);
      if (!result) return;
      if (item.showChangeCapacity) {
        item.reason = null;
      }
      const arr = [];
      this.alternatives_sync.forEach((x, i) =>
        arr.push(i === index ? item : x)
      );
      this.alternatives_sync = arr;
    },
    async remove(item, index) {
      if (this.readonly) return;
      const confirm = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar la configuración de la alternativa ${item.description}?`,
        'Sí',
        'No'
      );
      if (!confirm) return;
      const response = await api.deleteAlternativeLocation(item.id);

      if (response.status) {
        this.ShowToast(
          '',
          'La alternativa ha sido eliminada exitosamente.',
          'success'
        );
        this.alternatives_sync.splice(index, 1);
      } else {
        const message =
          response.data.response.data.message ||
          'No fue posible eliminar la alternativa.';
        this.ShowToast('Error', message, 'error');
      }
    },
    cancel(item, toggle) {
      item.showChangeCapacity = false;
      item.showDeterminate = false;
      toggle();
    },
    setViewModelCategories(categories) {
      this.localViewmodel.categories = categories;
    },
    setViewModelReasons(reasons) {
      this.localViewmodel.reasons = reasons;
    },
    setViewModelSalonSpecialties(salonSpecialties) {
      this.localViewmodel.salonSpecialties = salonSpecialties;
    },
    setViewModelNames(names) {
      this.localViewmodel.names = [...this.localViewmodel.names, ...names];
    },
    setViewModelDocumentTypes(documentTypes) {
      this.localViewmodel.documentTypes = documentTypes;
    },
    async saveUpdate(item) {
      const formData = objectToFormData(item, { indices: true });
      if (item.documents?.length) {
        item.documents.forEach((doc, i) =>
          formData.append(`documents[${i}].file`, doc.file)
        );
      }
      const response = await api.updateLocationAlternative(
        {
          schoolId: this.viewmodel.school.id,
          academicYearId: this.viewmodel.academicYear.id,
        },
        formData
      );
      if (response.status) {
        this.ShowToast(
          '',
          'La alternativa de ubicación ha sido guardada exitosamente.',
          'success'
        );
        this.$refs.alternativeForm.cancel();
        if (!item.id) {
          item.id = response.data.id;
          item.active = true;
          this.alternatives_sync.push(item);
        }
        item.updatedQuota = response.data.updatedQuota;
        item.updatedQuotaReason = response.data.updatedQuotaReason;
        this.$emit('update:alternatives', this.alternatives_sync);
        return true;
      } else {
        const message =
          response.data.response.data.message ||
          'La alternativa de ubicación no fue guardada.';
        this.ShowToast('Error', message, 'error');
        return false;
      }
    },
  },
};
</script>
